import { NOTE_NUMBER_LANGUAGES } from '../config';

import { joinProtocolValues } from './templateDraft';

const numberLanguage = (language = '') =>
  NOTE_NUMBER_LANGUAGES[language.trim().toLowerCase()];

const DECIMAL = /^-?[0-9]+(\.[0-9]+)?$/;

// A number typed by the clinician as scribe stores it: "." as the separator,
// a comma taken for it, and at most `precision` digits after it, as scribe's
// rule (`IsNoteDecimal`) takes; null when scribe would refuse it.
export const parseNoteDecimal = (typed: string, precision = 0) => {
  const number = typed.trim().replace(',', '.');
  if (!DECIMAL.test(number)) {
    return null;
  }
  const [, fraction = ''] = number.split('.');
  return fraction.length <= precision ? number : null;
};

// A stored number in the template language's decimal form.
export const formatNoteDecimal = (value: string, language?: string) =>
  value.replace('.', numberLanguage(language)?.decimalSeparator ?? '.');

// A stored number with its unit as scribe prints it: after a space, or
// attached for a degree or a percent.
export const printNoteNumber = (
  value: string,
  unit: string | undefined,
  language?: string,
) => {
  const printed = formatNoteDecimal(value, language);
  const label = unit ? numberLanguage(language)?.unitLabels[unit] ?? unit : '';
  if (!label) {
    return printed;
  }
  return label === '°' || label === '%'
    ? `${printed}${label}`
    : `${printed} ${label}`;
};

// A unit's label in the template's language.
export const printNoteUnit = (unit: string | undefined, language?: string) =>
  unit ? numberLanguage(language)?.unitLabels[unit] ?? unit : '';

// A value whose unit ends with a period ("мес.") before the line's own
// period leaves its period to the line, so the sentence ends once.
export const endNoteValueBeforePeriod = (text: string, periodAfter: boolean) =>
  periodAfter && text.endsWith('.') ? text.slice(0, -1) : text;

// A tooth surface's letter in the template's language.
export const printNoteSurface = (surface: string, language?: string) =>
  numberLanguage(language)?.surfaceLetters[surface] ?? surface;

// A number field's values as scribe prints them (`numbersText`): each with
// the unit, after its qualifier's word, joined by ", ".
export const printNoteNumbers = (
  numbers: { Qualifier: number; Value: string }[],
  unit: string | undefined,
  language?: string,
) =>
  joinProtocolValues(
    numbers.map(({ Qualifier, Value }) => {
      const printed = printNoteNumber(Value, unit, language);
      const qualifier = Qualifier
        ? numberLanguage(language)?.qualifierLabels[Qualifier]
        : '';
      return qualifier ? `${qualifier} ${printed}` : printed;
    }),
  );

// The word a qualifier reads as in the template's language.
export const printNoteQualifier = (qualifier: number, language?: string) =>
  numberLanguage(language)?.qualifierLabels[qualifier] ?? '';

// Whether a stored number lies within a field's range, either end open.
export const isNoteNumberInRange = (
  value: string,
  { Min, Max }: { Min?: string; Max?: string },
) =>
  (!Min || Number(value) >= Number(Min)) &&
  (!Max || Number(value) <= Number(Max));
